import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, ScrollView } from 'react-native';
import { compareDesc, format } from 'date-fns/esm';
import { generateClient } from 'aws-amplify/api';
import { listIntegrationLogs } from '@alucio/aws-beacon-amplify/src/graphql/queries';
import { IntegrationLog, IntegrationSettings, IntegrationRunStatus } from '@alucio/aws-beacon-amplify/src/models';
import {
  DNABox,
  DNAButton,
  DNACard,
  DNADivider,
  DNAModal, DNAText, GridList, Iffy,
  useTheme,
  util,
} from '@alucio/lux-ui';
import { DNAChip, DNAModalVariant } from 'src/components/DNA';

const styles = StyleSheet.create({
  modalContainer: {
    minWidth: 1000,
    minHeight: 752,
    flex: 1,
  },
  modalHeaderContainer: {
    margin: 21,
    fill: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  bodyTitle: {
    marginBottom: 0,
    margin: 24,
  },
})

const DNAIntegrationLogsModal: DNAModalVariant<{
  integrationSettings?: IntegrationSettings
}> = (props) => {
  const { integrationSettings, closeModal } = props
  const [integrationLogsList, setIntegrationLogsList] = useState<IntegrationLog[]>([] as IntegrationLog[])
  const [showLogRow, setShowLogRow] = useState<boolean>(false)
  const [selectedLogRow, setSelectedLogRow] = useState<IntegrationLog | null>(null)
  const theme = useTheme()

  const allStyle = {
    paddingHorizontal: theme['row-padding-horizontal'],
    paddingVertical: theme['row-padding-vertical'],
  }

  const refreshData = () => {
    getData()
  }

  const handleRowClick = (currentRow: IntegrationLog) => {
    setShowLogRow(true)
    setSelectedLogRow(currentRow)
  }

  const formatTimestamp = (time: string) => {
    return format(new Date(time), 'MM/dd/yyyy hh:mm:ss aaaa')
  }

  const handleBackPress = () => {
    setShowLogRow(false)
    setSelectedLogRow(null)
  }

  const getData = useCallback(async () => {
    const appsyncClient = generateClient();
    const { data } = await appsyncClient.graphql({
      query: listIntegrationLogs,
      variables: {
        filter: { integrationId: { eq: integrationSettings?.id } },
      },
    });

    const integrationLogs = data?.listIntegrationLogs?.items
    if (integrationLogs) {
      // @ts-ignore TODO we are checking for this variable but TS is still throwing a warning
      setIntegrationLogsList(integrationLogs)
    }
  }, [])

  useEffect(() => {
    getData()
  }, [])

  const getLogStatus = (logStatus: keyof typeof IntegrationRunStatus) => {
    switch (logStatus) {
      case IntegrationRunStatus.RUNNING:
        return 'primary'
      case IntegrationRunStatus.COMPLETED:
        return 'success'
      case IntegrationRunStatus.ERROR:
        return 'danger'
      case IntegrationRunStatus.WARNING:
        return 'warning'
      default:
        return 'danger'
    }
  }

  return (
    <DNAModal style={styles.modalContainer}>
      <Iffy is={!showLogRow}>
        <DNAModal.Header onClose={closeModal} style={styles.modalHeaderContainer}>
          <DNABox appearance="row" spacing="between" fill alignY="center">
            <DNAText>
              Status Log
            </DNAText>
            <DNAButton
              appearance="outline"
              status="secondary"
              onPress={refreshData}
              style={{ marginLeft: 20 }}
            >
              Refresh log
            </DNAButton>
          </DNABox>
        </DNAModal.Header>
        <DNAModal.Body>
          <DNABox as={ScrollView} style={{ width: 1000, height: 696 }} appearance="col" spacing="md" fill>
            <Iffy is={integrationLogsList.length}>
              <DNABox appearance="row" spacing="between" style={styles.bodyTitle}>
                <DNAText>Timestamp</DNAText>
                <DNAText>Status</DNAText>
              </DNABox>
              <DNADivider />
              {
                integrationLogsList.sort((a, b) => {
                  // @ts-ignore
                  return compareDesc(new Date(a.createdAt), new Date(b.createdAt))
                }).map((integrationLog) => {
                  const logStatus = getLogStatus(integrationLog.status)
                  // @ts-ignore
                  const timestamp = formatTimestamp(integrationLog.createdAt)
                  return (
                    <>
                      <DNACard
                        appearance="flat"
                        style={util.mergeStyles(props, allStyle)}
                        interactive="pressable"
                        onPress={() => handleRowClick(integrationLog)}
                      >
                        <DNABox key={integrationLog.id} appearance="row" spacing="between">
                          {/* @ts-ignore */}
                          <DNAText>{timestamp}</DNAText>
                          <DNAChip.Beacon status={logStatus} children={integrationLog.status} />
                        </DNABox>
                      </DNACard>
                      <DNADivider />
                    </>
                  )
                })
              }
            </Iffy>
            <Iffy is={!integrationLogsList.length}>
              <DNAText style={{ marginTop: 24 }}>No records for this integratoin log</DNAText>
            </Iffy>
          </DNABox>
        </DNAModal.Body>
      </Iffy>
      <Iffy is={showLogRow}>
        <DNAModal.Header onClose={closeModal} style={styles.modalHeaderContainer}>
          <DNABox appearance="row" spacing="between" fill alignY="center">
            <DNAButton
              appearance="ghost"
              status="secondary"
              iconLeft="caret-left"
              onPress={handleBackPress}
              style={{ marginRight: 30 }}
            />
            <DNAText>
              {/* @ts-ignore */}
              {selectedLogRow?.createdAt && formatTimestamp(selectedLogRow?.createdAt)}
            </DNAText>
            <DNAChip.Beacon
              status={getLogStatus(selectedLogRow?.status!)}
              style={{ marginLeft: 15 }}
              children={selectedLogRow?.status}
            />
          </DNABox>
        </DNAModal.Header>
        <DNAModal.Body>
          <DNABox as={ScrollView} style={{ width: 'inherit', height: 696 }} appearance="col" spacing="md" fill>
            <Iffy is={selectedLogRow}>
              {/* @ts-ignore TS is not aware that GridList.ROW as a DNACard gets the DNACard props ie appearance */}
              <GridList.Row as={DNACard} appearance="flat">
                {selectedLogRow?.log?.sort((a, b) => {
                  return (
                    (a?.timestamp || b?.timestamp) ? compareDesc(new Date(a?.timestamp!), new Date(b?.timestamp!)) : 0
                  )
                }).map((log) => {
                  return (
                    <GridList.Col>
                      <DNABox spacing="lg" style={styles.bodyTitle}>
                        <DNAText>{log?.timestamp && formatTimestamp(log?.timestamp)}</DNAText>
                        <DNAText>{log?.level}</DNAText>
                        <DNAText>{log?.srcDocId}</DNAText>
                        <DNAText>{log?.message}</DNAText>
                      </DNABox>
                    </GridList.Col>
                  )
                })}
              </GridList.Row>
            </Iffy>
            <Iffy is={!selectedLogRow}>
              <DNAText style={{ marginTop: 24 }}>No records for this integratoin log</DNAText>
            </Iffy>
          </DNABox>
        </DNAModal.Body>
      </Iffy>
    </DNAModal>
  )
}

export default DNAIntegrationLogsModal
